/// <reference types="@types/google.maps" />
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { AsyncPipe, isPlatformBrowser } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@jsverse/transloco';
import { GoogleMapsAutoCompleteSuggestionService, GoogleMapsScriptService } from '@rma/generic/maps/api';
import { BaseControlValueAccessor } from '@rma/generic/ui/controls/base-control';
import { MatErrorOverrideComponent } from '@rma/generic/ui/controls/mat-error';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { IsTranslationObjectPipe, KeyOrTranslationObject } from '@rma/generic/util/translation-object';
import { debounceTime, distinctUntilChanged, filter, Observable, of, switchMap } from 'rxjs';
import { Address } from '../address.types';

const isAutocompletePrediction = (
  result: google.maps.places.PlacePrediction | { description: Address },
): result is google.maps.places.PlacePrediction => {
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  return (result as google.maps.places.PlacePrediction).placeId !== undefined;
};

@Component({
  selector: 'rma-address-autocomplete',
  templateUrl: './address-autocomplete.component.html',
  imports: [
    AsyncPipe,
    CdkTrapFocus,
    IsTranslationObjectPipe,
    MatAutocompleteModule,
    MatErrorOverrideComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    RmaIconComponent,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AddressAutocompleteComponent),
    },
  ],
})
export class AddressAutocompleteComponent extends BaseControlValueAccessor<Address> implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly googleMapsAutoCompleteSuggestionService = inject(GoogleMapsAutoCompleteSuggestionService);
  private readonly googleMapsScriptService = inject(GoogleMapsScriptService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  public readonly required = input(false, { transform: booleanAttribute });
  public readonly disable = input(true, { transform: booleanAttribute });
  public readonly autoFocus = input(true, { transform: booleanAttribute });
  public readonly prefix = input(true, { transform: booleanAttribute });
  public readonly label = input<KeyOrTranslationObject>({
    key: 'form.label.property-address',
  });
  // Hides the example-format hint under the field. Defaults to false so it shows everywhere.
  public readonly hideHint = input(false, { transform: booleanAttribute });
  // Optional custom placeholder shown in the input; empty leaves it to the floating label.
  public readonly placeholder = input('');

  // Built in ngOnInit because it needs the bound `value`.
  public inputControl!: FormControl;
  public options$: Observable<google.maps.places.PlacePrediction[]> = of([]);
  public mapsReady$: Observable<boolean>;

  constructor() {
    super();
    this.mapsReady$ = this.googleMapsScriptService.appendScript();

    effect(() => {
      const disable = this.disable();

      if (disable) {
        this.inputControl.disable();
      } else {
        this.inputControl.enable();
      }
    });
  }

  ngOnInit() {
    this.inputControl = this.fb.control({ description: this.value }, this.required() ? Validators.required : null);

    if (this.isBrowser) {
      this.options$ = this.getAutoCompleteOptions();
    }
  }

  public onBlur(): void {
    this.onTouched();
  }

  public displayFn(result: google.maps.places.PlacePrediction | { description: Address }): string {
    return isAutocompletePrediction(result) ? result.text.text : (result.description?.fullAddress ?? '');
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.googleMapsAutoCompleteSuggestionService
      .addressToGeocoder(event.option.value.text.text)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.mapToAddress(result));
  }

  private getAutoCompleteOptions(): Observable<google.maps.places.PlacePrediction[]> {
    return this.inputControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter((value) => !!value && !isAutocompletePrediction(value)),
      switchMap((value) => this.googleMapsAutoCompleteSuggestionService.search(value)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private mapToAddress(place: google.maps.GeocoderResult[]) {
    const address = this.googleMapsAutoCompleteSuggestionService.mapPredictionToType<Address>(place);

    if (address) {
      address.fullAddress = place[0].formatted_address;
      address.displayAddress = place[0].formatted_address;

      this.cdr.detectChanges();
      this.updateValue(address);
    }
  }
}
